import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash, PencilSimple, EnvelopeSimple, Eye, EyeSlash, MagnifyingGlass } from "@phosphor-icons/react";

export default function Utenti() {
  const { user } = useAuth();
  const [utenti, setUtenti] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [comuni, setComuni] = useState(null);
  const [destinatari, setDestinatari] = useState("");

  const isMaster = user?.role === "master";

  const load = () => api.get("/utenti/master").then((r) => setUtenti(r.data)).catch(() => {});
  useEffect(() => { if (isMaster) load(); }, [user]);

  if (!isMaster)
    return (
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="utenti-forbidden">
        <p className="font-semibold">Accesso riservato al ruolo Master.</p>
      </div>
    );

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name || "", email: u.email, password: "" });
    setShowPwd(false);
  };

  const saveEdit = async () => {
    try {
      const payload = { name: editForm.name, email: editForm.email };
      if (editForm.password) payload.password = editForm.password;
      await api.put(`/utenti/master/${editing.id}`, payload);
      toast.success("Utente aggiornato");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openComuni = (u) => {
    setComuni(u);
    setDestinatari(u.comunicazioni_destinatari || "");
  };

  const saveComuni = async () => {
    try {
      await api.put(`/utenti/master/${comuni.id}/comunicazioni`, { destinatari });
      toast.success("Destinatari salvati");
      setComuni(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'utente Master "${u.email}"?`)) return;
    try {
      await api.delete(`/utenti/master/${u.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q ? utenti.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q)) : utenti;

  return (
    <div className="space-y-6 fade-in" data-testid="utenti-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-[#6B7280] font-semibold">Pannello Master</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold mt-1">Gestione utenti</h1>
        <p className="text-[#6B7280] mt-1" data-testid="utenti-count">{utenti.length} utenti Master registrati — sincronizzati dal portale AreaPAQ.</p>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per email..."
          data-testid="utenti-search-input"
          className="pl-10 rounded-full bg-white"
        />
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
        <Table data-testid="utenti-table">
          <TableHeader className="bg-[#FAF9F6]">
            <TableRow>
              <TableHead>Utente</TableHead>
              <TableHead>Ruolo</TableHead>
              <TableHead>Creato il</TableHead>
              <TableHead>Comunicazioni</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-[#6B7280] py-10" data-testid="utenti-empty">Nessun utente Master trovato.</TableCell>
              </TableRow>
            )}
            {filtered.map((u) => (
              <TableRow key={u.id} className="hover:bg-stone-50" data-testid={`utente-row-${u.id}`}>
                <TableCell>
                  <p className="font-semibold">{u.name || "—"} {u.id === user.id && <span className="text-xs text-[#6B7280] font-normal">(tu)</span>}</p>
                  <p className="text-xs text-[#6B7280]">{u.email}</p>
                </TableCell>
                <TableCell>
                  <Badge className="rounded-full bg-amber-100 text-amber-800 hover:bg-amber-100">Master</Badge>
                </TableCell>
                <TableCell>{u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}</TableCell>
                <TableCell className="text-sm text-[#6B7280] max-w-[220px] truncate">{u.comunicazioni_destinatari || u.email}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={() => openComuni(u)} data-testid={`utente-comunicazioni-${u.id}`} className="hover:bg-stone-100" title="Comunicazioni">
                    <EnvelopeSimple size={18} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`utente-edit-${u.id}`} className="hover:bg-stone-100" title="Modifica">
                    <PencilSimple size={18} />
                  </Button>
                  {u.id !== user.id && (
                    <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`utente-delete-${u.id}`} className="hover:bg-red-50 text-[#D32F2F]" title="Elimina">
                      <Trash size={18} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="utente-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifica utente</DialogTitle>
            <DialogDescription>Aggiorna i dati dell'utente Master.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nome</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} data-testid="utente-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} data-testid="utente-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Password <span className="text-xs text-[#6B7280]">(lascia vuoto per non cambiare)</span></Label>
              <div className="relative">
                <Input type={showPwd ? "text" : "password"} value={editForm.password} onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))} data-testid="utente-password-input" className="pr-10" />
                <button type="button" onClick={() => setShowPwd((s) => !s)} data-testid="utente-password-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280]">
                  {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Ruolo</Label>
              <div><Badge className="rounded-full bg-amber-100 text-amber-800 hover:bg-amber-100">Master</Badge></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Annulla</Button>
            <Button onClick={saveEdit} data-testid="utente-save-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F]">Salva modifiche</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!comuni} onOpenChange={(v) => !v && setComuni(null)}>
        <DialogContent className="max-w-md" data-testid="utente-comunicazioni-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><EnvelopeSimple size={20} weight="duotone" className="text-[#D95D39]" /> Comunicazioni per {comuni?.email}</DialogTitle>
            <DialogDescription>
              Indirizzi email a cui questo utente Master riceve le comunicazioni di servizio (backup, notifiche di sistema). La configurazione SMTP si gestisce nella sezione Impostazioni.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Destinatari</Label>
            <Input value={destinatari} onChange={(e) => setDestinatari(e.target.value)} placeholder={comuni?.email} data-testid="utente-destinatari-input" />
            <p className="text-xs text-[#6B7280]">Separa più indirizzi con virgola. Se vuoto viene usata l'email dell'account.</p>
          </div>
          <DialogFooter>
            <Button onClick={saveComuni} data-testid="utente-destinatari-save-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F]">Salva destinatari</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
